<!-- src/views/admin/Clients.vue -->
<template>
  <admin-layout title="Gestion des clients">
    <div class="mb-6 flex justify-between items-center">
      <div class="w-64">
        <input type="text" 
               placeholder="Rechercher un client..." 
               v-model="searchQuery"
               class="form-control"
               @input="handleSearch">
      </div>
      
      <button @click="showCreateModal = true" class="btn btn-primary">
        Ajouter un client
      </button>
    </div>
    
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>
    
    <div v-else-if="clients.length === 0" class="bg-white rounded-lg shadow p-6 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">Aucun client trouvé</h3>
      <p class="mt-1 text-sm text-gray-500">
        Commencez par ajouter un client pour gérer ses dispositifs.
      </p>
      <div class="mt-6">
        <button @click="showCreateModal = true" class="btn btn-primary">
          Ajouter un client
        </button>
      </div>
    </div>
    
    <div v-else class="bg-white shadow overflow-hidden sm:rounded-md">
      <ul class="divide-y divide-gray-200">
        <li v-for="client in clients" :key="client.id">
          <div class="px-4 py-4 flex items-center sm:px-6 hover:bg-gray-50">
            <div class="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <div class="flex text-sm">
                  <p class="font-medium text-primary-600 truncate">{{ client.name }}</p>
                  <p class="ml-1 flex-shrink-0 font-normal text-gray-500">
                    <span v-if="!client.active" class="ml-2 text-red-500">
                      (Désactivé)
                    </span>
                    <!-- Suppression demandée par le titulaire (B9) : compte suspendu, effacé à l'échéance. -->
                    <span v-if="client.suppression" class="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800"
                          :title="`Effacement le ${new Date(client.suppression.effacement_le).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}`">
                      Suppression demandée
                    </span>
                  </p>
                </div>
                <div class="mt-2 flex">
                  <div class="flex items-center text-sm text-gray-500">
                    <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p>
                      {{ client.contact_name }}
                    </p>
                  </div>
                  <div class="ml-6 flex items-center text-sm text-gray-500">
                    <svg class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p>
                      {{ client.email }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5">
                <div class="flex space-x-2">
                  <button @click="viewClient(client.id)" class="btn-primary text-xs px-3 py-1 rounded">
                    Détails
                  </button>
                  <button v-if="client.active" 
                          @click="toggleClientStatus(client.id, false)" 
                          class="btn-warning text-xs px-3 py-1 rounded"
                          :disabled="actionLoading">
                    Désactiver
                  </button>
                  <button v-else 
                          @click="toggleClientStatus(client.id, true)" 
                          class="btn-success text-xs px-3 py-1 rounded"
                          :disabled="actionLoading">
                    Activer
                  </button>
                  <button @click="confirmDelete(client)" 
                          class="btn-danger text-xs px-3 py-1 rounded"
                          :disabled="actionLoading">
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
    
    <!-- Modal de création de client -->
    <div v-if="showCreateModal" class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="showCreateModal = false"></div>
        
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Ajouter un client
                </h3>
                <div class="mt-4">
                  <div v-if="createError" class="mb-4 text-sm text-red-600">
                    {{ createError }}
                  </div>
                  
                  <form @submit.prevent="createClient">
                    <div class="mb-4">
                      <label for="name" class="form-label">Nom du client</label>
                      <input type="text" id="name" v-model="newClient.name" class="form-control" required>
                    </div>
                    
                    <div class="mb-4">
                      <label for="contact_name" class="form-label">Nom du contact</label>
                      <input type="text" id="contact_name" v-model="newClient.contact_name" class="form-control" required>
                    </div>
                    
                    <div class="mb-4">
                      <label for="email" class="form-label">Email</label>
                      <input type="email" id="email" v-model="newClient.email" class="form-control" required>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button" 
                    @click="createClient" 
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                    :disabled="createLoading">
              <span v-if="createLoading" class="mr-2">
                <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0112 20c4.411 0 8-3.589 8-8h-4a4 4 0 00-4 4v0"></path>
</svg>
              </span>
              Créer
            </button>
<button type="button" @click="showCreateModal = false" 
        class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"> 
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de confirmation de suppression -->
    <div v-if="clientToDelete" class="fixed z-10 inset-0 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="clientToDelete = null"></div>
        
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Supprimer le client
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Êtes-vous sûr de vouloir supprimer le client "{{ clientToDelete.name }}" ? Cette action supprimera également tous les dispositifs associés et ne peut pas être annulée.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button" 
                    @click="deleteClient" 
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    :disabled="deleteLoading">
              <span v-if="deleteLoading" class="mr-2">
                <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              Supprimer
            </button>
            <button type="button" 
                    @click="clientToDelete = null" 
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  </admin-layout>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import AdminLayout from '@/components/layout/AdminLayout.vue';

export default defineComponent({
  name: 'AdminClients',
  
  components: {
    AdminLayout
  },
  
  setup() {
    const store = useStore();
    const router = useRouter();
    
    const clients = ref([]);
    const loading = ref(true);
    const actionLoading = ref(false);
    const searchQuery = ref('');
    
    // Modal de création de client
    const showCreateModal = ref(false);
    const createLoading = ref(false);
    const createError = ref('');
    const newClient = ref({
      name: '',
      contact_name: '',
      email: ''
    });
    
    // Modal de suppression
    const clientToDelete = ref(null);
    const deleteLoading = ref(false);
    
    // Chargement initial des clients
    onMounted(async () => {
      try {
        const result = await store.dispatch('admin/fetchClients');
        clients.value = result;
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error);
      } finally {
        loading.value = false;
      }
    });
    
    // Recherche de clients
    const handleSearch = async () => {
      if (!searchQuery.value.trim()) {
        try {
          const result = await store.dispatch('admin/fetchClients');
          clients.value = result;
        } catch (error) {
          console.error('Erreur lors du chargement des clients:', error);
        }
        return;
      }
      
      loading.value = true;
      
      try {
        const result = await store.dispatch('admin/searchClients', searchQuery.value);
        clients.value = result;
      } catch (error) {
        console.error('Erreur lors de la recherche de clients:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // Voir les détails d'un client
    const viewClient = (clientId) => {
      router.push(`/admin/clients/${clientId}`);
    };
    
    // Activer/désactiver un client
    const toggleClientStatus = async (clientId, active) => {
      actionLoading.value = true;
      
      try {
        await store.dispatch('admin/toggleClientStatus', { clientId, active });
        
        // Mettre à jour l'état du client dans la liste
        const clientIndex = clients.value.findIndex(c => c.id === clientId);
        if (clientIndex !== -1) {
          clients.value[clientIndex].active = active;
        }
      } catch (error) {
        console.error('Erreur lors du changement de statut du client:', error);
      } finally {
        actionLoading.value = false;
      }
    };
    
    // Confirmation de suppression d'un client
    const confirmDelete = (client) => {
      clientToDelete.value = client;
    };
    
    // Suppression d'un client
    const deleteClient = async () => {
      if (!clientToDelete.value) return;
      
      deleteLoading.value = true;
      
      try {
        await store.dispatch('admin/deleteClient', clientToDelete.value.id);
        
        // Supprimer le client de la liste
        clients.value = clients.value.filter(c => c.id !== clientToDelete.value.id);
        
        // Fermer le modal
        clientToDelete.value = null;
      } catch (error) {
        console.error('Erreur lors de la suppression du client:', error);
      } finally {
        deleteLoading.value = false;
      }
    };
    
    // Création d'un client
    const createClient = async () => {
      createLoading.value = true;
      createError.value = '';
      
      try {
        const result = await store.dispatch('admin/createClient', newClient.value);
        
        // Ajouter le nouveau client à la liste
        clients.value.push({
          id: result.clientId,
          ...newClient.value,
          active: true
        });
        
        // Réinitialiser le formulaire
        newClient.value = {
          name: '',
          contact_name: '',
          email: ''
        };
        
        // Fermer le modal
        showCreateModal.value = false;
      } catch (error) {
        console.error('Erreur lors de la création du client:', error);
        createError.value = error.response?.data?.message || 'Erreur lors de la création du client';
      } finally {
        createLoading.value = false;
      }
    };
    
    return {
      clients,
      loading,
      actionLoading,
      searchQuery,
      handleSearch,
      viewClient,
      toggleClientStatus,
      showCreateModal,
      createLoading,
      createError,
      newClient,
      createClient,
      clientToDelete,
      deleteLoading,
      confirmDelete,
      deleteClient
    };
  }
});
</script>
